/**
 * Why a saver waits:
 * - `debounce`: a change waits for the autosave delay. The Publish button is off ("Saving…").
 * - `undo`: a delete waits for its Undo window. The Publish button stays on; a click sends the delete first.
 */
export type WaitingSaveKind = 'debounce' | 'undo';

/** What the Publish button reads. A new object only when a value changes. */
export interface EditorSavesSnapshot {
  /** A change waits for its autosave delay, or a save is in flight. */
  isBusy: boolean;
  /** A delete waits for its Undo window: a change that a publish would send first. */
  hasUndoWaiting: boolean;
  /**
   * The open block forms with a wrong field (QA finding brk-16). Their change is not saved, so a
   * publish would publish the old value: the Publish button is off ("Fix 1 block first").
   */
  invalidCount: number;
}

const IDLE: EditorSavesSnapshot = { isBusy: false, hasUndoWaiting: false, invalidCount: 0 };

export interface FlushAllResult {
  /** False when a save that the flush waited for failed, or the saves did not settle. */
  ok: boolean;
}

/** A flush calls the flushers again until nothing waits. More rounds than this means a saver does not stop. */
const MAX_FLUSH_ROUNDS = 20;

/**
 * The page-wide save tracker of the editor. It knows the savers that wait (a debounce timer or an
 * Undo window) and the saves in flight. "Publish changes" calls `flushAll` first, so a change that
 * waits or runs reaches the draft before the publish.
 */
export class EditorSaveTracker {
  private readonly waiting = new Map<string, { flush: () => void; kind: WaitingSaveKind }>();

  private readonly inFlight = new Set<Promise<void>>();

  private readonly invalid = new Set<string>();

  private readonly listeners = new Set<() => void>();

  private failures = 0;

  private snapshot: EditorSavesSnapshot = IDLE;

  /** A saver waits: `flush` sends its change now. The same key again replaces the entry. */
  register(key: string, flush: () => void, kind: WaitingSaveKind = 'debounce'): void {
    this.waiting.set(key, { flush, kind });
    this.changed();
  }

  /** The saver sent its change, or has nothing to send now. */
  unregister(key: string): void {
    if (this.waiting.delete(key)) {
      this.changed();
    }
  }

  /** A form has a wrong field (`true`), or its fields are right again or it closed (`false`). */
  setInvalid(key: string, isInvalid: boolean): void {
    const changed = isInvalid ? !this.invalid.has(key) : this.invalid.has(key);
    if (!changed) {
      return;
    }
    if (isInvalid) {
      this.invalid.add(key);
    } else {
      this.invalid.delete(key);
    }
    this.changed();
  }

  /**
   * Tracks a save call until it settles. A rejected promise is a failed save; `isFailure` also marks
   * a resolved value as failed (an RTK result with an `error`). Returns the same promise.
   */
  track<T>(promise: Promise<T>, isFailure?: (value: T) => boolean): Promise<T> {
    const settled = promise.then(
      (value) => {
        if (isFailure?.(value) === true) {
          this.failures += 1;
        }
      },
      () => {
        this.failures += 1;
      }
    );
    this.inFlight.add(settled);
    this.changed();
    settled
      .then(() => {
        this.inFlight.delete(settled);
        this.changed();
      })
      .catch(console.error);
    return promise;
  }

  /** True while a change waits for its autosave delay or a save is in flight. An Undo window does not count. */
  isBusy = (): boolean => this.snapshot.isBusy;

  getSnapshot = (): EditorSavesSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * Sends every change that waits, then waits for every save in flight, until nothing is left. A
   * failed save does not throw: the result says it.
   */
  async flushAll(): Promise<FlushAllResult> {
    const failuresBefore = this.failures;
    for (let round = 0; round < MAX_FLUSH_ROUNDS; round += 1) {
      const flushers = [...this.waiting.values()];
      if (flushers.length === 0 && this.inFlight.size === 0) {
        return { ok: this.failures === failuresBefore };
      }
      flushers.forEach(({ flush }) => {
        try {
          flush();
        } catch (error) {
          console.error(error);
          this.failures += 1;
        }
      });
      // A flusher starts its save now: wait for it too.
      await Promise.allSettled([...this.inFlight]);
    }
    return { ok: false };
  }

  private changed(): void {
    const kinds = [...this.waiting.values()].map((entry) => entry.kind);
    const isBusy = this.inFlight.size > 0 || kinds.includes('debounce');
    const hasUndoWaiting = kinds.includes('undo');
    const invalidCount = this.invalid.size;
    if (
      isBusy === this.snapshot.isBusy &&
      hasUndoWaiting === this.snapshot.hasUndoWaiting &&
      invalidCount === this.snapshot.invalidCount
    ) {
      return;
    }
    this.snapshot = { isBusy, hasUndoWaiting, invalidCount };
    this.listeners.forEach((listener) => listener());
  }
}
